import { AUTO_DETECT } from "./languages";

// The free endpoint the Google Translate widget itself calls, so no key is needed.
const ENDPOINT = "https://translate.googleapis.com/translate_a/single";
// The text rides in the query string, so long text is sent in pieces.
const MAX_CHUNK_LENGTH = 1200;

// Splits on paragraph, then sentence, then hard length so a chunk never cuts a word.
const splitIntoSentences = (text) => {
    const pieces = [];
    let start = 0;

    for (let i = 0; i < text.length; i++) {
        const char = text[i];
        const isBreak = char === "\n" || ".!?\u3002\uFF01\uFF1F".indexOf(char) >= 0;
        // A sentence ends only where the punctuation is followed by a space or a newline.
        if (isBreak && (char === "\n" || /\s/.test(text[i + 1] || " "))) {
            pieces.push(text.slice(start, i + 1));
            start = i + 1;
        }
    }

    if (start < text.length) pieces.push(text.slice(start));
    return pieces;
};

const splitIntoChunks = (text) => {
    const chunks = [];
    let buffer = "";

    const pushBuffer = () => {
        if (buffer) chunks.push(buffer);
        buffer = "";
    };

    splitIntoSentences(text).forEach((piece) => {
        if (piece.length > MAX_CHUNK_LENGTH) {
            pushBuffer();
            for (let i = 0; i < piece.length; i += MAX_CHUNK_LENGTH) {
                chunks.push(piece.slice(i, i + MAX_CHUNK_LENGTH));
            }
            return;
        }
        if ((buffer + piece).length > MAX_CHUNK_LENGTH) pushBuffer();
        buffer += piece;
    });

    pushBuffer();
    return chunks.length ? chunks : [text];
};

const translateChunk = async (chunk, sourceLanguage, targetLanguage) => {
    const params = new URLSearchParams({
        client: "gtx",
        sl: sourceLanguage || AUTO_DETECT,
        tl: targetLanguage,
        dt: "t",
        q: chunk,
    });

    const response = await fetch(`${ENDPOINT}?${params.toString()}`);
    if (!response.ok) throw new Error(`Google translate responded with ${response.status}`);

    const data = await response.json();
    const segments = Array.isArray(data?.[0]) ? data[0] : [];
    return {
        text: segments.map((segment) => segment?.[0] || "").join(""),
        detectedLanguage: data?.[2] || null,
    };
};

// Frontend fallback: the browser calls Google directly, so nothing goes through our backend.
export const googleTranslator = {
    id: "google",
    label: "Google translate",

    async isAvailable() {
        return typeof fetch === "function";
    },

    async translate({ text, sourceLanguage, targetLanguage, onStatus }) {
        const chunks = splitIntoChunks(text);
        const translatedChunks = [];
        let detectedLanguage = null;

        for (let i = 0; i < chunks.length; i++) {
            onStatus?.(chunks.length > 1 ? `Translating... part ${i + 1} of ${chunks.length}` : "Translating...");
            const result = await translateChunk(chunks[i], sourceLanguage, targetLanguage);
            translatedChunks.push(result.text);
            if (!detectedLanguage) detectedLanguage = result.detectedLanguage;
        }

        return { text: translatedChunks.join(""), detectedLanguage };
    },
};
